(function (root) {
  "use strict";

  if (!root || root.DyeforStorageAdapter) return;

  var DEFAULT_API_BASE = "https://fsimg.youniq.co.uk/api/v1";
  var DEFAULT_PUBLIC_BASE = "https://fsimg.youniq.co.uk";
  var DIRECT_PART_ATTEMPTS = 3;
  var MAX_FRAGMENT_ATTEMPTS = 10;
  var MAX_API_ATTEMPTS = 10;
  var PART_TIMEOUT_MS = 300000;
  var installed = false;

  function abortError() {
    return new DOMException("Upload cancelled", "AbortError");
  }

  function sha256Hex(blob) {
    if (!root.crypto || !root.crypto.subtle || typeof blob.arrayBuffer !== "function") return Promise.resolve(null);
    return blob.arrayBuffer().then(function (buffer) {
      return root.crypto.subtle.digest("SHA-256", buffer);
    }).then(function (digest) {
      return Array.prototype.map.call(new Uint8Array(digest), function (byte) {
        return byte.toString(16).padStart(2, "0");
      }).join("");
    }).catch(function () { return null; });
  }

  function uploadRequestId() {
    if (root.crypto && typeof root.crypto.randomUUID === "function") return root.crypto.randomUUID();
    return "upl_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  }

  function uploadTimeout(options) {
    var configured = Number(options && options.timeout || 0);
    return Number.isFinite(configured) && configured > 0 ? Math.max(configured, PART_TIMEOUT_MS) : PART_TIMEOUT_MS;
  }

  function uploadConcurrency() {
    var connection = root.navigator && root.navigator.connection;
    if (!connection) return 3;
    if (connection.saveData || /(^|-)2g$/i.test(String(connection.effectiveType || ""))) return 1;
    if (/3g$/i.test(String(connection.effectiveType || ""))) return 2;
    return 3;
  }

  function retryDelay(attempt) {
    var base = Math.min(30000, 1000 * Math.pow(2, Math.max(0, attempt - 1)));
    return base + Math.floor(Math.random() * Math.max(1, Math.round(base * 0.25)));
  }

  function retryableRequest(error) {
    if (!error || !error.status) return true;
    return error.status === 408 || error.status === 425 || error.status === 429 || error.status >= 500;
  }

  function waitForRetry(state, options, filename, attempt, location) {
    if (state.cancelled) return Promise.reject(abortError());
    if (typeof options.onRetry === "function") {
      options.onRetry({ location: location, filename: filename, attempt: attempt + 1 });
    }
    return new Promise(function (resolve, reject) {
      var timer = null;
      var listening = false;
      var settled = false;
      var delay = retryDelay(attempt);

      function cleanup() {
        if (timer) root.clearTimeout(timer);
        if (listening && typeof root.removeEventListener === "function") root.removeEventListener("online", online);
        state.waiters.delete(cancel);
      }
      function finish() {
        if (settled) return;
        settled = true;
        cleanup();
        if (state.cancelled) return reject(abortError());
        resolve();
      }
      function schedule() {
        if (settled) return;
        timer = root.setTimeout(finish, delay);
      }
      function online() {
        listening = false;
        schedule();
      }
      function cancel() {
        if (settled) return;
        settled = true;
        cleanup();
        reject(abortError());
      }

      state.waiters.add(cancel);
      if (root.navigator && root.navigator.onLine === false && typeof root.addEventListener === "function") {
        listening = true;
        root.addEventListener("online", online);
      } else {
        schedule();
      }
    });
  }

  function provider() {
    return "s3";
  }

  function apiBase() {
    return String(root.DYEFOR_ASSET_API_BASE || DEFAULT_API_BASE).replace(/\/$/, "");
  }

  function publicBase() {
    return String(root.DYEFOR_ASSET_PUBLIC_BASE || DEFAULT_PUBLIC_BASE).replace(/\/$/, "");
  }

  function extensionType(filename) {
    var extension = String(filename || "").toLowerCase().split(".").pop();
    if (extension === "jpg" || extension === "jpeg") return "image/jpeg";
    if (extension === "png") return "image/png";
    if (extension === "webp") return "image/webp";
    if (extension === "bmp") return "image/bmp";
    if (extension === "heic") return "image/heic";
    if (extension === "heif") return "image/heif";
    return "application/octet-stream";
  }

  function dataUriBlob(value) {
    var match = String(value).match(/^data:([^;,]+)?(;base64)?,(.*)$/s);
    if (!match) throw new Error("Unsupported upload source");
    var type = match[1] || "application/octet-stream";
    var binary = match[2] ? atob(match[3]) : decodeURIComponent(match[3]);
    var bytes = new Uint8Array(binary.length);
    for (var index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return new Blob([bytes], { type: type });
  }

  function asBlob(input, filename) {
    if (input instanceof Blob) return input;
    if (input instanceof ArrayBuffer) return new Blob([input], { type: extensionType(filename) });
    if (ArrayBuffer.isView(input)) {
      return new Blob([input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength)], { type: extensionType(filename) });
    }
    if (typeof input === "string" && input.indexOf("data:") === 0) return dataUriBlob(input);
    throw new Error("Unsupported upload source");
  }

  function requestJson(url, options, controller) {
    var requestOptions = Object.assign({}, options || {});
    requestOptions.headers = Object.assign({ "content-type": "application/json" }, requestOptions.headers || {});
    if (controller) requestOptions.signal = controller.signal;
    return root.fetch(url, requestOptions).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (payload) {
        if (!response.ok) {
          var error = new Error(payload.message || "The image upload failed.");
          error.code = payload.code || "upload_failed";
          error.status = response.status;
          throw error;
        }
        return payload;
      });
    });
  }

  function requestJsonWithRetry(url, requestOptions, controller, state, uploadOptions, filename, attempt) {
    return requestJson(url, requestOptions, controller).catch(function (error) {
      if (state.cancelled) throw abortError();
      if (attempt >= MAX_API_ATTEMPTS || !retryableRequest(error)) throw error;
      return waitForRetry(state, uploadOptions, filename, attempt, "upload-service").then(function () {
        return requestJsonWithRetry(url, requestOptions, controller, state, uploadOptions, filename, attempt + 1);
      });
    });
  }

  function applyPlan(state, plan) {
    state.plan = Object.assign({}, state.plan || {}, plan);
    state.parts = {};
    (plan.parts || []).forEach(function (part) { state.parts[part.partNumber] = part; });
    return state.plan;
  }

  function refreshPlan(state, options, filename, controller) {
    if (state.refreshPromise) return state.refreshPromise;
    var refreshUrl = apiBase() + "/uploads/" + encodeURIComponent(state.plan.assetId) + "/refresh";
    var pending = requestJsonWithRetry(refreshUrl, {
      method: "POST",
      body: "{}",
    }, controller, state, options, filename, 1).then(function (plan) {
      applyPlan(state, plan);
      state.refreshPromise = null;
      return state.plan;
    }, function (error) {
      state.refreshPromise = null;
      throw error;
    });
    state.refreshPromise = pending;
    return pending;
  }

  function reportProgress(state, blob, options) {
    var loaded = Object.keys(state.loaded).reduce(function (sum, key) { return sum + state.loaded[key]; }, 0);
    if (typeof options.onProgress === "function") {
      options.onProgress({
        totalBytes: loaded,
        totalPercent: Math.min(100, Math.round((loaded / blob.size) * 100)),
      });
    }
  }

  function applyFallbackPlan(state, plan) {
    state.fallbackPlans[plan.partNumber] = plan;
    return plan;
  }

  function fallbackPlan(state, part, options, filename, force) {
    var current = state.fallbackPlans[part.partNumber];
    if (!force && current && Date.now() < Number(current.expiresAt || 0) - 60000) return Promise.resolve(current);
    if (state.fallbackPlanPromises[part.partNumber]) return state.fallbackPlanPromises[part.partNumber];
    var url = apiBase() + "/uploads/" + encodeURIComponent(state.plan.assetId) + "/parts/" + part.partNumber + "/fragments";
    var pending = requestJsonWithRetry(url, {
      method: "POST",
      body: "{}",
    }, state.controller, state, options, filename, 1).then(function (plan) {
      state.fallbackPlanPromises[part.partNumber] = null;
      return applyFallbackPlan(state, plan);
    }, function (error) {
      state.fallbackPlanPromises[part.partNumber] = null;
      throw error;
    });
    state.fallbackPlanPromises[part.partNumber] = pending;
    return pending;
  }

  function uploadFragment(fragment, part, blob, state, options, filename, attempt) {
    return new Promise(function (resolve, reject) {
      if (state.cancelled) return reject(abortError());
      var plan = state.fallbackPlans[part.partNumber];
      var current = (plan.fragments || []).filter(function (candidate) {
        return candidate.fragmentNumber === fragment.fragmentNumber;
      })[0] || fragment;
      var xhr = new XMLHttpRequest();
      var retrying = false;
      state.requests.add(xhr);
      xhr.open("PUT", current.url, true);
      xhr.timeout = uploadTimeout(options);
      xhr.upload.onprogress = function (event) {
        if (!event.lengthComputable) return;
        var partProgress = state.fragmentLoaded[part.partNumber] || {};
        partProgress[current.fragmentNumber] = event.loaded;
        state.fragmentLoaded[part.partNumber] = partProgress;
        state.loaded[part.partNumber] = Object.keys(partProgress).reduce(function (sum, key) {
          return sum + partProgress[key];
        }, 0);
        reportProgress(state, blob, options);
      };
      xhr.onerror = retryOrReject;
      xhr.ontimeout = retryOrReject;
      xhr.onabort = function () {
        if (state.cancelled) reject(abortError());
        else retryOrReject();
      };
      xhr.onload = function () {
        state.requests.delete(xhr);
        if (xhr.status >= 200 && xhr.status < 300) {
          var partProgress = state.fragmentLoaded[part.partNumber] || {};
          partProgress[current.fragmentNumber] = current.end - current.start;
          state.fragmentLoaded[part.partNumber] = partProgress;
          state.loaded[part.partNumber] = Object.keys(partProgress).reduce(function (sum, key) {
            return sum + partProgress[key];
          }, 0);
          reportProgress(state, blob, options);
          return resolve();
        }
        retryOrReject();
      };
      function retryOrReject() {
        if (retrying) return;
        retrying = true;
        state.requests.delete(xhr);
        if (state.cancelled) return reject(abortError());
        if (attempt < MAX_FRAGMENT_ATTEMPTS) {
          return waitForRetry(state, options, filename, attempt, "s3-adaptive").then(function () {
            var expired = Date.now() > Number(plan.expiresAt || 0) - 60000;
            var forbidden = xhr.status === 401 || xhr.status === 403;
            return expired || forbidden ? fallbackPlan(state, part, options, filename, true) : plan;
          }).then(function (latestPlan) {
            var latest = (latestPlan.fragments || []).filter(function (candidate) {
              return candidate.fragmentNumber === current.fragmentNumber;
            })[0] || current;
            return uploadFragment(latest, part, blob, state, options, filename, attempt + 1);
          }).then(resolve, reject);
        }
        reject(new Error("A fragment of the image could not be uploaded"));
      }
      xhr.send(blob.slice(current.start, current.end));
    });
  }

  function adaptiveUploadPart(part, blob, state, options, filename) {
    return fallbackPlan(state, part, options, filename, false).then(function (plan) {
      state.fragmentLoaded[part.partNumber] = {};
      state.loaded[part.partNumber] = 0;
      return runPool(plan.fragments, uploadConcurrency(), function (fragment) {
        return uploadFragment(fragment, part, blob, state, options, filename, 1);
      });
    }).then(function () {
      var url = apiBase() + "/uploads/" + encodeURIComponent(state.plan.assetId) + "/parts/" + part.partNumber + "/assemble";
      return requestJsonWithRetry(url, {
        method: "POST",
        body: "{}",
      }, state.controller, state, options, filename, 1);
    }).then(function (result) {
      state.loaded[part.partNumber] = part.end - part.start;
      reportProgress(state, blob, options);
      return result;
    });
  }

  function uploadPart(part, blob, state, options, filename, attempt) {
    return new Promise(function (resolve, reject) {
      if (state.cancelled) return reject(abortError());
      var currentPart = state.parts[part.partNumber] || part;
      var xhr = new XMLHttpRequest();
      var retrying = false;
      state.requests.add(xhr);
      xhr.open("PUT", currentPart.url, true);
      xhr.timeout = uploadTimeout(options);
      xhr.upload.onprogress = function (event) {
        if (!event.lengthComputable) return;
        state.loaded[currentPart.partNumber] = event.loaded;
        reportProgress(state, blob, options);
      };
      xhr.onerror = retryOrReject;
      xhr.ontimeout = retryOrReject;
      xhr.onabort = function () {
        if (state.cancelled) reject(abortError());
        else retryOrReject();
      };
      xhr.onload = function () {
        state.requests.delete(xhr);
        if (xhr.status >= 200 && xhr.status < 300) {
          var etag = xhr.getResponseHeader("etag");
          if (!etag) return reject(new Error("S3 did not return an upload ETag"));
          state.loaded[currentPart.partNumber] = currentPart.end - currentPart.start;
          return resolve({ partNumber: currentPart.partNumber, etag: etag });
        }
        retryOrReject();
      };
      function retryOrReject() {
        if (retrying) return;
        retrying = true;
        state.requests.delete(xhr);
        if (state.cancelled) return reject(abortError());
        if (attempt < DIRECT_PART_ATTEMPTS) {
          return waitForRetry(state, options, filename, attempt, "s3").then(function () {
            var expired = state.plan && Date.now() > Number(state.plan.expiresAt || 0) - 60000;
            var forbidden = xhr.status === 401 || xhr.status === 403;
            return expired || forbidden ? refreshPlan(state, options, filename, state.controller) : null;
          }).then(function () {
            return uploadPart(state.parts[currentPart.partNumber] || currentPart, blob, state, options, filename, attempt + 1);
          }).then(resolve, reject);
        }
        return waitForRetry(state, options, filename, attempt, "s3-adaptive").then(function () {
          return adaptiveUploadPart(currentPart, blob, state, options, filename);
        }).then(resolve, reject);
      }
      xhr.send(blob.slice(currentPart.start, currentPart.end));
    });
  }

  function runPool(items, concurrency, worker) {
    var next = 0;
    var results = new Array(items.length);
    function run() {
      var index = next;
      next += 1;
      if (index >= items.length) return Promise.resolve();
      return worker(items[index], index).then(function (result) {
        results[index] = result;
        return run();
      });
    }
    var workers = [];
    for (var count = 0; count < Math.min(concurrency, items.length); count += 1) workers.push(run());
    return Promise.all(workers).then(function () { return results; });
  }

  function s3Upload(input, uploadOptions, storeOptions, token) {
    uploadOptions = uploadOptions || {};
    storeOptions = storeOptions || {};
    token = token || {};
    var filename = String(storeOptions.filename || input && input.name || "upload");
    var blob = asBlob(input, filename);
    var contentType = blob.type || extensionType(filename);
    var checksumPromise = sha256Hex(blob);
    var controller = new AbortController();
    var state = {
      cancelled: false,
      requests: new Set(),
      waiters: new Set(),
      loaded: {},
      fragmentLoaded: {},
      parts: {},
      fallbackPlans: {},
      fallbackPlanPromises: {},
      plan: null,
      refreshPromise: null,
      controller: controller,
      clientRequestId: uploadRequestId(),
    };
    token.cancel = function () {
      state.userCancelled = true;
      state.cancelled = true;
      controller.abort();
      state.requests.forEach(function (request) { request.abort(); });
      state.waiters.forEach(function (cancel) { cancel(); });
      if (state.plan && state.plan.abortUrl) {
        root.fetch(apiBase() + state.plan.abortUrl.replace(/^\/api\/v1/, ""), { method: "DELETE", keepalive: true }).catch(function () {});
      }
    };
    token.abort = token.cancel;

    return requestJsonWithRetry(apiBase() + "/uploads", {
      method: "POST",
      body: JSON.stringify({
        filename: filename,
        contentType: contentType,
        size: blob.size,
        sessionPath: storeOptions.path || null,
        clientRequestId: state.clientRequestId,
      }),
    }, controller, state, uploadOptions, filename, 1).then(function (plan) {
      applyPlan(state, plan);
      return runPool(plan.parts, uploadConcurrency(), function (part) {
        return uploadPart(part, blob, state, uploadOptions, filename, 1);
      }).then(function (parts) {
        return checksumPromise.then(function (checksumSha256) {
          return requestJsonWithRetry(apiBase() + "/uploads/" + encodeURIComponent(plan.assetId) + "/complete", {
            method: "POST",
            body: JSON.stringify({ parts: parts, checksumSha256: checksumSha256 }),
          }, controller, state, uploadOptions, filename, 1);
        });
      });
    }).then(function (result) {
      if (typeof uploadOptions.onProgress === "function") {
        uploadOptions.onProgress({ totalBytes: blob.size, totalPercent: 100 });
      }
      if (result && /^s3_[a-f0-9]{32}$/.test(String(result.handle || ""))) {
        result.url = publicBase() + "/s3/" + encodeURIComponent(result.handle);
      }
      return result;
    }).catch(function (error) {
      var userCancelled = state.userCancelled;
      state.cancelled = true;
      controller.abort();
      state.requests.forEach(function (request) { request.abort(); });
      state.waiters.forEach(function (cancel) { cancel(); });
      if (state.plan && !userCancelled) {
        root.fetch(apiBase() + "/uploads/" + encodeURIComponent(state.plan.assetId), { method: "DELETE" }).catch(function () {});
      }
      throw error;
    });
  }

  function assetReference(value) {
    var raw = String(value || "");
    var newId = raw.match(/(?:^|\/)(s3_[a-f0-9]{32})(?:$|[/?#])/);
    if (newId) return { kind: "s3", id: newId[1] };
    if (/^s3_[a-f0-9]{32}$/.test(raw)) return { kind: "s3", id: raw };
    var legacyUrl = raw.match(/\/legacy\/([A-Za-z0-9_-]{16,40})(?:$|[/?#])/);
    if (legacyUrl) return { kind: "legacy", id: legacyUrl[1] };
    var filestack = legacyFilestackReference(raw);
    if (filestack) return { kind: "legacy", id: filestack.id };
    if (/^[A-Za-z0-9]{20}$/.test(raw)) return { kind: "legacy", id: raw };
    return null;
  }

  function legacyFilestackReference(value) {
    var raw = String(value || "");
    if (!/cdn\.filestackcontent\.com/i.test(raw)) return null;
    var decoded = raw;
    try { decoded = decodeURIComponent(raw); } catch (error) {}
    var path = decoded.replace(/^https?:\/\/cdn\.filestackcontent\.com\//i, "").split("?")[0];
    var segments = path.split("/").filter(Boolean);
    var id = null;
    var rotate = 0;
    var crop = null;
    segments.forEach(function (segment) {
      if (/^[A-Za-z0-9]{20}$/.test(segment)) id = segment;
      var rotateMatch = segment.match(/^rotate=deg:(-?\d+)$/i);
      if (rotateMatch) rotate = parseInt(rotateMatch[1], 10);
      var cropMatch = segment.match(/^crop=dim:\[?(\d+),(\d+),(\d+),(\d+)\]?$/i);
      if (cropMatch) crop = cropMatch.slice(1).map(function (part) { return parseInt(part, 10); });
    });
    return id ? { id: id, rotate: rotate, crop: crop } : null;
  }

  function normalizeLegacyUrl(value) {
    var raw = String(value || "").replace("doimage.workers.dev", "youniq.co.uk");
    var reference = legacyFilestackReference(raw);
    if (!reference) return raw;
    return transformUrl(reference.id, {
      rotate: { deg: reference.rotate },
      crop: reference.crop ? { dim: reference.crop } : null,
    });
  }

  function transformUrl(handle, options) {
    var reference = assetReference(handle);
    if (!reference) return null;
    var url = new URL(publicBase() + "/" + reference.kind + "/" + encodeURIComponent(reference.id));
    var rotate = Number(options && options.rotate && options.rotate.deg || 0);
    if (Number.isFinite(rotate) && rotate) url.searchParams.set("rotate", String(rotate));
    var crop = options && options.crop && options.crop.dim;
    if (Array.isArray(crop) && crop.length === 4) url.searchParams.set("crop", crop.map(function (value) { return parseInt(value, 10); }).join(","));
    return url.toString();
  }

  function ensureCropOriginalSize() {
    var node = root.selNodeObj;
    var current = node && node.attrs && node.attrs.origSize;
    if (Number(current && current.width) > 0 && Number(current && current.height) > 0) return false;
    if (!node || !node.attrs || !root.cropper || typeof root.cropper.getImageData !== "function") return false;

    var image = root.cropper.getImageData();
    var width = Number(image && (image.naturalWidth || image.width) || 0);
    var height = Number(image && (image.naturalHeight || image.height) || 0);
    if (!(width > 0 && height > 0)) return false;

    node.attrs.origSize = {
      width: width,
      height: height,
      aspectRatio: width / height,
    };
    return true;
  }

  function installCropCompatibility() {
    var original = root.ExecuteCrop;
    if (typeof original !== "function" || original.__dyeforValidSizeGuard) return;

    function executeCropWithValidSize() {
      ensureCropOriginalSize();
      return original.apply(this, arguments);
    }
    executeCropWithValidSize.__dyeforValidSizeGuard = true;
    root.ExecuteCrop = executeCropWithValidSize;
  }

  function install(options) {
    options = options || {};
    if (options.apiBase) root.DYEFOR_ASSET_API_BASE = options.apiBase;
    if (options.publicBase) root.DYEFOR_ASSET_PUBLIC_BASE = options.publicBase;
    root.DYEFOR_STORAGE_PROVIDER = "s3";
    if (installed) return;
    root.filestack = {
      init: function () {
        return {
          upload: s3Upload,
          transform: function (handle, transformOptions) {
            return transformUrl(handle, transformOptions) || normalizeLegacyUrl(handle);
          },
        };
      },
    };
    root.fixSrcFilestack = normalizeLegacyUrl;
    root.cartfixSrcFilestack = normalizeLegacyUrl;
    installCropCompatibility();
    installed = true;
  }

  root.DyeforStorageAdapter = {
    install: install,
    provider: provider,
    transformUrl: transformUrl,
    assetReference: assetReference,
    normalizeLegacyUrl: normalizeLegacyUrl,
    _test: {
      dataUriBlob: dataUriBlob,
      extensionType: extensionType,
      legacyFilestackReference: legacyFilestackReference,
      retryDelay: retryDelay,
      retryableRequest: retryableRequest,
      sha256Hex: sha256Hex,
      uploadTimeout: uploadTimeout,
      uploadRequestId: uploadRequestId,
      uploadConcurrency: uploadConcurrency,
      ensureCropOriginalSize: ensureCropOriginalSize,
    },
  };
})(typeof window !== "undefined" ? window : globalThis);
